import React from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import { Divider, Grid, Stack, Typography } from '@mui/material';
import { useGetCaseActionById } from 'pages/Cases/hooks/useGetCaseActionById';
import { useGetCaseById } from 'pages/Cases/hooks/useGetCaseById';
import BackButton from 'components/BackButton';
import GreyFrame from 'components/GrayFrame';
import SummaryHeader from 'pages/Cases/components/SummaryHeader';
import ModelSetsTable from 'pages/Cases/components/ModelSetsTable';
import ModelSetsFeesTable from 'pages/Cases/components/ModelSetsFeesTable';
import TabsLayout from 'components/TabsLayout/TabsLayout';
import ModelSetsPortfolioCharacteristicsTable from 'pages/Cases/components/ModelSetsPortfolioCharacteristicsTable';
import { calculateAdditionalCharacteristics } from 'pages/Cases/hooks/usePostCalculatePortfolioCharacteristics';
import CircularProgressBar from 'components/CircularProgressBar';

const modelSetsTabs = [
  {
    label: 'Model Sets',
    path: '',
    validPatterns: [
      'cases/:caseId/portfolio-constructions/:portfolioId/model-sets/:modelSetName',
    ],
    replaceLocation: true,
  },
  {
    label: 'Portfolio Characteristics',
    path: 'portfolio-characteristics',
    replaceLocation: true,
  },
];

const ModelSets: React.FC = () => {
  const { actionId, caseId, modelSetName } = useParams();
  const { caseData, isCaseLoading } = useGetCaseById({ id: caseId! });
  const { action, isActionLoading, refetchAction } = useGetCaseActionById({
    actionId: actionId || '',
    caseId: caseId || '',
  });

  if (isActionLoading || isCaseLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center" height="100%">
        <CircularProgressBar size={60} />
      </Grid>
    );
  }

  // fees for the model set from the action
  const modelSetFees = action.value.model_set_fees.find(
    (modelSetFee: any) => modelSetFee.model_set_name === modelSetName,
  );

  // portfolio characteristics from the action
  const portfolioCharacteristics = action.value.model_set_characteristics.find(
    (modelSetCharacteristic: any) =>
      modelSetCharacteristic.model_set_name === modelSetName,
  );

  // portfolio characteristics from the action with additional characteristics meant to render the table
  const modelSetPortfolioCharacteristics = {
    ...portfolioCharacteristics,
    portfolio_options: portfolioCharacteristics.portfolio_options.map(
      option => ({
        ...option,
        portfolios: option.portfolios.map(portfolio =>
          calculateAdditionalCharacteristics(portfolio),
        ),
      }),
    ),
  };

  return (
    <Stack
      sx={{
        overflowY: 'auto',
        width: '100%',
        height: '100%',
        paddingBottom: '48px',
      }}
      direction="column"
    >
      <Grid container sx={{ padding: '16px' }}>
        <Grid item xs={12} sx={{ marginBottom: '16px' }}>
          <BackButton name="Summary" />
        </Grid>
        <GreyFrame>
          <Grid
            container
            item
            xs={12}
            sx={{ padding: '16px' }}
            justifyContent="space-between"
            alignItems="center"
          >
            <SummaryHeader caseData={caseData} action={action} />
            <Grid item xs={6} sx={{ textAlign: 'right' }}>
              <Typography
                gutterBottom
                sx={{ fontSize: '16px', fontWeight: '600' }}
              >
                {modelSetName}
              </Typography>
            </Grid>
          </Grid>
          <Divider />
          <Grid container sx={{ padding: '16px' }}>
            <Routes>
              <Route
                element={
                  <TabsLayout
                    tabs={modelSetsTabs}
                    removeGreyFrame
                    paddingBottom="0px"
                    paddingRight="0px"
                    marginBottom="0px"
                  />
                }
              >
                <Route
                  path="/"
                  element={
                    <>
                      {!isActionLoading && (
                        <ModelSetsTable
                          action={action}
                          // slideMode={true}
                          isDraft={action.isDraft}
                          modelSetName={modelSetName || ''}
                          refetchAction={refetchAction}
                        />
                      )}
                      {!isActionLoading && modelSetFees && (
                        <ModelSetsFeesTable fees={modelSetFees} />
                      )}
                    </>
                  }
                />
                <Route
                  path="/portfolio-characteristics"
                  element={
                    !isActionLoading &&
                    modelSetPortfolioCharacteristics && (
                      <ModelSetsPortfolioCharacteristicsTable
                        portfolioCharacteristics={
                          modelSetPortfolioCharacteristics
                        }
                      />
                    )
                  }
                />
              </Route>
            </Routes>
          </Grid>
        </GreyFrame>
      </Grid>
    </Stack>
  );
};

export default ModelSets;
